/**
 * Field Ops — geography helpers (demo, 2026-11-04).
 *
 * The real system has NO coordinates for customers today (verified 2026-10-29). This demo gives every store a
 * latitude/longitude close to its real city so the map, the distances, the ETAs and the "nearby stores" suggestions
 * can be validated with the managers. In production the coordinates will come from geocoding each customer address
 * once (stored with the customer), and the device GPS will give the live position.
 *
 * No optional chaining / nullish coalescing: this project's CRA version (react-scripts 2.1.3) can't compile them.
 */

export const CITY = {
    'Chattanooga, TN': [35.0456, -85.3097], 'Cleveland, TN': [35.1595, -84.8766], 'Dalton, GA': [34.7698, -84.9702],
    'Albertville, AL': [34.2676, -86.2089], 'Boaz, AL': [34.2007, -86.1664], 'Guntersville, AL': [34.3582, -86.2947],
    'Rome, GA': [34.257, -85.1647], 'Cedartown, GA': [34.0112, -85.2552], 'Cartersville, GA': [34.1651, -84.7999],
    'Knoxville, TN': [35.9606, -83.9207], 'Maryville, TN': [35.7565, -83.9705], 'Sevierville, TN': [35.8681, -83.5619],
    'Mobile, AL': [30.6954, -88.0399], 'Montgomery, AL': [32.3668, -86.3], 'Prattville, AL': [32.464, -86.4597],
    'Birmingham, AL': [33.5186, -86.8104], 'Hoover, AL': [33.4054, -86.8114], 'Bessemer, AL': [33.4018, -86.9544],
    'Tuscaloosa, AL': [33.2098, -87.5692], 'Northport, AL': [33.229, -87.5772],
    'Columbus, GA': [32.461, -84.9877], 'Opelika, AL': [32.6454, -85.3783], 'Auburn, AL': [32.6099, -85.4808],
    'Atlanta, GA': [33.749, -84.388], 'Marietta, GA': [33.9526, -84.5499], 'Norcross, GA': [33.9412, -84.2135],
    'Gainesville, GA': [34.2979, -83.8241], 'Buford, GA': [34.1209, -84.0044],
    'Valdosta, GA': [30.8327, -83.2785], 'Tifton, GA': [31.4505, -83.5085], 'Moultrie, GA': [31.1799, -83.789],
    'Kennesaw, GA': [34.0234, -84.6155], 'Calhoun, GA': [34.5026, -84.951],
    'Florence, AL': [34.7998, -87.6773], 'Muscle Shoals, AL': [34.7448, -87.6675],
    'Huntsville, AL': [34.7304, -86.5861], 'Gadsden, AL': [34.0143, -86.0066], 'Athens, GA': [33.9519, -83.3576],
    'Macon, GA': [32.8407, -83.6324], 'Augusta, GA': [33.4735, -81.9748], 'Savannah, GA': [32.0809, -81.0912],
    'Anniston, AL': [33.6598, -85.8316], 'Dothan, AL': [31.2232, -85.3905], 'Albany, GA': [31.5785, -84.1557],
}

/** The demo warehouse where drivers load (near Marietta, GA). Placeholder: replace with the real address. */
export const DEPOT = { name: 'Main warehouse (demo)', lat: 33.9, lng: -84.52 }

/** Tiny seeded generator so the same store always lands on the same spot. */
export const seededRandom = (seed) => {
    let s = seed
    return () => {
        s = (s * 1664525 + 1013904223) % 4294967296
        return s / 4294967296
    }
}

/** Coordinates of a city name with a seeded scatter (≈ up to 3.5 miles around the centre). */
export const placeInCity = (city, rnd) => {
    const c = CITY[city] || CITY['Atlanta, GA']
    return { lat: Math.round((c[0] + (rnd() - 0.5) * 0.09) * 100000) / 100000, lng: Math.round((c[1] + (rnd() - 0.5) * 0.11) * 100000) / 100000 }
}

const toRad = (d) => (d * Math.PI) / 180

/** Straight-line distance in miles between two {lat,lng}. */
export const miles = (a, b) => {
    if (!a || !b) return 0
    const R = 3958.8
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const x = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
    return 2 * R * Math.asin(Math.sqrt(x))
}

/** Estimated driving miles (straight line × 1.28, the usual city/rural road factor). */
export const roadMiles = (a, b) => miles(a, b) * 1.28
/** Estimated driving minutes at a blended 32 mph (local roads + short highway legs). */
export const driveMinutes = (mi) => Math.round((mi / 32) * 60)

export const fmtMiles = (mi) => (mi < 0.1 ? '0 mi' : mi < 10 ? mi.toFixed(1) + ' mi' : Math.round(mi) + ' mi')
export const fmtMinutes = (m) => {
    if (m < 1) return '<1 min'
    if (m < 60) return m + ' min'
    const h = Math.floor(m / 60)
    const r = m % 60
    return h + ' h' + (r ? ' ' + r + ' min' : '')
}
/** 8:05 AM style from minutes after midnight. */
export const clockLabel = (minutes) => {
    const m = ((Math.round(minutes) % 1440) + 1440) % 1440
    const h = Math.floor(m / 60)
    const mm = m % 60
    return (h % 12 === 0 ? 12 : h % 12) + ':' + (mm < 10 ? '0' : '') + mm + ' ' + (h < 12 ? 'AM' : 'PM')
}
export const minutesOfDay = (ms) => { const d = new Date(ms); return d.getHours() * 60 + d.getMinutes() }
export const clockOfStamp = (ms) => clockLabel(minutesOfDay(ms))

/** Total driving miles through a list of {lat,lng} (optionally starting at `from`). */
export const pathMiles = (points, from) => {
    let total = 0
    let prev = from || null
    points.forEach((p) => { if (prev) total += roadMiles(prev, p); prev = p })
    return total
}

/** Nearest-neighbour ordering from `from`. Returns the points re-ordered (items keep any extra properties). */
export const orderNearest = (items, from) => {
    const left = items.slice()
    const out = []
    let cur = from
    while (left.length > 0) {
        let best = 0
        let bestD = Infinity
        for (let i = 0; i < left.length; i++) {
            const d = miles(cur, left[i])
            if (d < bestD) { bestD = d; best = i }
        }
        const next = left.splice(best, 1)[0]
        out.push(next)
        cur = next
    }
    return out
}

/**
 * Schedule estimate for an ordered list of stops. Each stop needs {lat,lng}. `startMin` = when the day starts
 * (minutes after midnight), `serviceMin` = minutes spent at each stop. Returns [{ arrive, leave, legMi, legMin }].
 */
export const buildEta = (stops, from, startMin, serviceMin) => {
    let t = startMin
    let prev = from
    return stops.map((s) => {
        const legMi = prev ? roadMiles(prev, s) : 0
        const legMin = driveMinutes(legMi)
        t += legMin
        const arrive = t
        t += serviceMin
        prev = s
        return { arrive: arrive, leave: t, legMi: legMi, legMin: legMin }
    })
}

/* ---------- deep links to the phone's maps app ---------- */
export const mapsPlaceUrl = (lat, lng, label) => 'https://www.google.com/maps/search/?api=1&query=' + lat + ',' + lng + (label ? '(' + encodeURIComponent(label) + ')' : '')
export const mapsNavigateUrl = (lat, lng) => 'https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng + '&travelmode=driving'
/** Whole-route link (Google Maps accepts up to 9 waypoints in one URL). */
export const mapsRouteUrl = (points, from) => {
    const list = points.slice(0, 10)
    if (list.length === 0) return ''
    const dest = list[list.length - 1]
    const wp = list.slice(0, -1).slice(0, 9).map((p) => p.lat + ',' + p.lng).join('|')
    return 'https://www.google.com/maps/dir/?api=1&destination=' + dest.lat + ',' + dest.lng + (from ? '&origin=' + from.lat + ',' + from.lng : '') + (wp ? '&waypoints=' + encodeURIComponent(wp) : '') + '&travelmode=driving'
}
export const telUrl = (phone) => 'tel:' + String(phone || '').replace(/[^0-9+]/g, '')

/* ---------- road geometry (OSRM public demo server; falls back to straight lines when it is unreachable) ---------- */
const roadCache = {}
/**
 * Fetches the driving geometry through the points. Resolves { coords: [[lat,lng],...], miles, minutes } or null.
 * The public OSRM demo server is fine for a demo; production should use its own routing service or Google/Mapbox.
 */
export const fetchRoadRoute = (points) => {
    if (!points || points.length < 2 || typeof fetch === 'undefined') return Promise.resolve(null)
    const key = points.map((p) => p.lat.toFixed(4) + ',' + p.lng.toFixed(4)).join(';')
    if (roadCache[key] !== undefined) return Promise.resolve(roadCache[key])
    const path = points.map((p) => p.lng + ',' + p.lat).join(';')
    const url = 'https://router.project-osrm.org/route/v1/driving/' + path + '?overview=full&geometries=geojson'
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = setTimeout(() => { if (ctl) ctl.abort() }, 7000)
    return fetch(url, ctl ? { signal: ctl.signal } : undefined)
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
            clearTimeout(timer)
            if (!j || !j.routes || !j.routes[0]) { roadCache[key] = null; return null }
            const r = j.routes[0]
            const out = { coords: r.geometry.coordinates.map((c) => [c[1], c[0]]), miles: r.distance / 1609.34, minutes: Math.round(r.duration / 60) }
            roadCache[key] = out
            return out
        })
        .catch(() => { clearTimeout(timer); roadCache[key] = null; return null })
}

/* ---------- nearby store suggestions ---------- */
/**
 * Scores candidate stores around a position. `candidates` = [{ store, gap, lastVisit, missedThisMonth, state }]
 * (built by store.nearbyCandidates). Higher score = better reason to stop by. Returns the list sorted, each with
 * `reasons` (short chips) and `dist` (straight-line miles).
 */
export const rankNearby = (pos, candidates, maxMi) => {
    const limit = maxMi || 15
    return candidates
        .map((c) => {
            const dist = miles(pos, c.store)
            let score = 0
            const reasons = []
            if (c.missedThisMonth > 0) { score += 30 + c.missedThisMonth * 6; reasons.push({ tone: 'red', text: 'Missed visit' + (c.missedThisMonth > 1 ? ' ×' + c.missedThisMonth : '') }) }
            if (c.state && c.state.key === 'behind') { score += 28; reasons.push({ tone: 'amber', text: 'Behind goal' + (c.gap > 0 ? ' · $' + Math.round(c.gap).toLocaleString('en-US') + ' to go' : '') }) }
            if (c.daysSince !== null && c.daysSince >= 14) { score += Math.min(25, c.daysSince / 2); reasons.push({ tone: 'purple', text: c.daysSince + ' days since last visit' }) }
            if (c.daysSince === null) { score += 15; reasons.push({ tone: 'purple', text: 'Not visited yet' }) }
            if (c.unrouted) { reasons.push({ tone: 'gray', text: 'No route yet' }) }
            score += Math.max(0, 40 - dist * 4)
            return { store: c.store, dist: dist, score: score, reasons: reasons, gap: c.gap, state: c.state, daysSince: c.daysSince, unrouted: !!c.unrouted }
        })
        .filter((x) => x.dist <= limit)
        .sort((a, b) => b.score - a.score)
}

/** "10–12 PM" → { start, end } in minutes after midnight (null when it can't be read). */
export const windowRange = (w) => {
    const nums = String(w || '').match(/\d+/g)
    if (!nums || nums.length < 2) return null
    const s = Number(nums[0])
    const e = Number(nums[1])
    const pm = /PM/i.test(w)
    const end24 = pm ? (e === 12 ? 12 : e + 12) : e
    const dur = ((e - s + 12) % 12) || 12
    return { start: (end24 - dur) * 60, end: end24 * 60 }
}
